import React, { memo } from 'react'

import { Button, classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { oneOf, array, func, object } from 'prop-types'

import getSelectedFilters from 'Utils/getSelectedFilters'

import WithListingPageContext from '../../WithListingPageContext'

const allowedPricingFilters = ['sale_off', 'subscribed', 'free']
const allowedModelTypeFilters = [
  'print_ready', 'animated', 'pbr', 'rigged', 'low_poly', 'with_makes', 'cgt_verified', 'multiple_parts',
]
const allowedDesignerTypeFilters = ['following']

function FiltersWrapperWithTags({
  type,
  currentSelectedFilters,
  setMultipleFilters,
  currentFilters,
  setSelectedFilters,
  setShouldResetPriceRange,
  trackFilterChange,
}) {
  function handleFilterChange(selectedValue, isActive) {
    if (type === 'pricing' && selectedValue === 'free') {
      setSelectedFilters({
        name: 'priceRange',
        value: undefined,
        skipFetch: true,
      })
      setShouldResetPriceRange(true)
    }

    const isRemoveFilterAction = currentSelectedFilters.includes(selectedValue)
    const wasActiveFilter = !isActive

    if (wasActiveFilter && !isRemoveFilterAction) {
      trackFilterChange({ name: type, value: selectedValue })
    } else {
      window.removeCurrentSearchFilter({ name: type, value: selectedValue })
    }

    const conflictingFilter =
      (selectedValue === 'free' && 'subscribed') || (selectedValue === 'subscribed' && 'free') || null

    const newFiltersEntries = isRemoveFilterAction
      ? currentSelectedFilters.filter((value) => value !== selectedValue).map((value) => [value, '1'])
      : [
          ...currentSelectedFilters.filter(
            (value) => value !== 'exclude_free' && value !== conflictingFilter
          ),
          selectedValue,
        ].map((value) => [value, '1'])
    const newFilters = Object.fromEntries(newFiltersEntries)

    setMultipleFilters({ name: type, value: newFilters })
  }

  function isFilterActive(value) {
    return currentSelectedFilters.includes(value)
  }

  function isFilterDisabled(value) {
    return (
      (value === 'free' && currentSelectedFilters.includes('sale_off')) ||
      (value === 'sale_off' && currentSelectedFilters.includes('free')) ||
      false
    )
  }

  const classNameByType = {
    modelType: 'filters-wrapper-with-buttons__model-type',
    pricing: 'filters-wrapper-with-buttons__pricing',
    designers: 'filters-wrapper-with-buttons__model-type',
  }

  return (
    <div className={classnames('filters-wrapper-with-buttons', classNameByType[type])}>
      {currentFilters.filters.map(({ label, value }) => {
        const isActive = isFilterActive(value)

        return (
          <Button
            type="secondary"
            size="medium"
            key={value}
            customClass="filters-wrapper-with-buttons__button"
            onClick={() => handleFilterChange(value, isActive)}
            isActive={isActive}
            disabled={isFilterDisabled(value)}
          >
            {label}
          </Button>
        )
      })}
    </div>
  )
}

FiltersWrapperWithTags.propTypes = {
  type: oneOf(['pricing', 'modelType', 'designers']).isRequired,
  currentSelectedFilters: array.isRequired,
  setMultipleFilters: func.isRequired,
  currentFilters: object.isRequired,
  setSelectedFilters: func.isRequired,
  setShouldResetPriceRange: func.isRequired,
  trackFilterChange: func.isRequired,
}

const mapStateToProps = (
  {
    selectedFilters,
    setSelectedFilters,
    setMultipleFilters,
    filtersOptions,
    setShouldResetPriceRange,
    trackFilterChange,
    subscriptionFlowEnabled,
  },
  { type }
) => {
  const isPricingGroup = type === 'pricing'
  const isModelTypeGroup = type === 'modelType'
  const isDesignerTypeGroup = type === 'designers'
  const currentFilters = filtersOptions[type]
  const { filters, label } = currentFilters
  const groupsSelectedFilters = selectedFilters[type]
  const currentSelectedFilters = getSelectedFilters(groupsSelectedFilters)

  const allowedFilters = (() => {
    if (isPricingGroup) {
      return filters.filter(({ value }) =>
        allowedPricingFilters.includes(value) && (value !== 'subscribed' || subscriptionFlowEnabled)
      )
    }

    if (isModelTypeGroup) {
      return filters.filter(({ value }) => allowedModelTypeFilters.includes(value))
    }

    if (isDesignerTypeGroup) {
      return filters.filter(({ value }) => allowedDesignerTypeFilters.includes(value))
    }

    return filters
  })()

  return {
    currentSelectedFilters,
    setSelectedFilters,
    setMultipleFilters,
    currentFilters: { label, filters: allowedFilters },
    setShouldResetPriceRange,
    trackFilterChange,
  }
}

export default WithListingPageContext(mapStateToProps)(memo(FiltersWrapperWithTags))
